import { useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HelpInput from "../../../../Inputs/HelpInput/HelpInput.tsx";
import {
  MassToCompensate,
  ValueOrNull,
  getMassUnits,
} from "../../../../../utils/helpers/tolerance/GeneralHelper.js";
import { toFixed } from "../../../../../utils/helpers/NumberHelper.js";
import { useUnits } from "../../../../../hooks/useMeasureInUnits.jsx";
import { HelpOutput } from "../../../../Inputs/HelpDisplay/HelpOutput.tsx";
import { useTranslation } from "react-i18next";
import UnitsRadioGroup from "../../../../Inputs/HelpDisplay/UnitsRadioGroup.jsx";
import ResultBox from "../../../../Styled/Box/ResultBox.jsx";
import InputStack from "../../../../Styled/Stack/InputStack.jsx";

const defaultCoefficient = 10e2;

const PermissibleUnbalancedMassCalculation = ({
  isDualPlane,
  residualDisbalancePerPlaneA,
  residualDisbalancePerPlaneB,
  residualDisbalance,
  radiusA,
  radiusB,
  radius,
  setRadiusA,
  setRadiusB,
  setRadius,
}) => {
  const { t } = useTranslation();

  const allowedMassUnits = useMemo(() => getMassUnits(t).slice(0, 3), [t]);

  const { unitLabel, unit, handleUnitChange } = useUnits(
    null,
    allowedMassUnits[1]
  );
  const massPerA = useMemo(
    () =>
      toFixed(
        (MassToCompensate(residualDisbalancePerPlaneA, radiusA) * unit.value) /
          defaultCoefficient
      ),
    [residualDisbalancePerPlaneA, radiusA, unit.value]
  );
  const massPerB = useMemo(
    () =>
      toFixed(
        (MassToCompensate(residualDisbalancePerPlaneB, radiusB) * unit.value) /
          defaultCoefficient
      ),
    [residualDisbalancePerPlaneB, radiusB, unit.value]
  );
  const massPer = useMemo(
    () =>
      toFixed(
        (MassToCompensate(residualDisbalance, radius) * unit.value) /
          defaultCoefficient
      ),
    [residualDisbalance, radius, unit.value]
  );

  const handleRadiusAChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value < 0) return;
      setRadiusA(value);
    },
    [setRadiusA]
  );
  const handleRadiusBChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value < 0) return;
      setRadiusB(value);
    },
    [setRadiusB]
  );
  const handleRadiusChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value < 0) return;
      setRadius(value);
    },
    [setRadius]
  );

  return (
    <Accordion sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h5">
          {t(
            "shared.disbalance.components.permissibleUnbalancedMassCalculation.title"
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {!isDualPlane && (
          <>
            <Typography sx={{ my: 2 }} variant="h6">
              {t(
                "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.radius.title"
              )}
            </Typography>
            <HelpInput
              label={<div>R, {t("units.length.mm")}</div>}
              variant="outlined"
              fullWidth
              value={radius}
              type="number"
              onChange={handleRadiusChange}
              sx={{ flexBasis: "100%" }}
              helpButtonText={t(
                "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.radius.help"
              )}
            />
          </>
        )}
        {isDualPlane && (
          <>
            <Typography sx={{ my: 2 }} variant="h6">
              {t(
                "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.radiusInPlanes.title"
              )}
            </Typography>
            <InputStack>
              <HelpInput
                label={
                  <div>
                    R<sub>A</sub>, {t("units.length.mm")}
                  </div>
                }
                variant="outlined"
                fullWidth
                value={radiusA}
                type="number"
                onChange={handleRadiusAChange}
                helpButtonText={t(
                  "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.radiusInPlanes.help",
                  { plane: "A" }
                )}
              />
              <HelpInput
                label={
                  <div>
                    R<sub>B</sub>, {t("units.length.mm")}
                  </div>
                }
                variant="outlined"
                fullWidth
                value={radiusB}
                type="number"
                onChange={handleRadiusBChange}
                helpButtonText={t(
                  "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.radiusInPlanes.help",
                  { plane: "B" }
                )}
              />
            </InputStack>
          </>
        )}
        <Typography variant="h6" sx={{ my: 2 }}>
          {t("units.mass.unitSelection")}
        </Typography>
        <UnitsRadioGroup
          value={unit}
          options={allowedMassUnits}
          onChange={handleUnitChange}
        />
        <Typography variant="h6" sx={{ my: 3 }}>
          {t("shared.disbalance.result")}
        </Typography>
        <ResultBox>
          {!isDualPlane && (
            <>
              <Typography sx={{ my: 2 }} variant="h6">
                {t(
                  "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.allowedMass.title"
                )}
              </Typography>
              <HelpOutput
                label={
                  <div>
                    m<sub>per</sub>, {unitLabel}
                  </div>
                }
                showEndAdornmentOnChildrenNull
                endAdornment={unitLabel}
                helpButtonText={t(
                  "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.allowedMass.help"
                )}
              >
                {ValueOrNull(massPer)}
              </HelpOutput>
            </>
          )}
          {isDualPlane && (
            <>
              <Typography sx={{ my: 2 }} variant="h6">
                {t(
                  "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.allowedMassInPlanes.title"
                )}
              </Typography>
              <HelpOutput
                label={
                  <div>
                    m<sub>per A</sub>, {unitLabel}
                  </div>
                }
                showEndAdornmentOnChildrenNull
                endAdornment={unitLabel}
                helpButtonText={t(
                  "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.allowedMassInPlanes.help",
                  { plane: "A" }
                )}
              >
                {ValueOrNull(massPerA)}
              </HelpOutput>
              <HelpOutput
                label={
                  <div>
                    m<sub>per B</sub>, {unitLabel}
                  </div>
                }
                showEndAdornmentOnChildrenNull
                endAdornment={unitLabel}
                helpButtonText={t(
                  "shared.disbalance.components.permissibleUnbalancedMassCalculation.components.allowedMassInPlanes.help",
                  { plane: "B" }
                )}
              >
                {ValueOrNull(massPerB)}
              </HelpOutput>
            </>
          )}
        </ResultBox>
      </AccordionDetails>
    </Accordion>
  );
};

export default PermissibleUnbalancedMassCalculation;
